import { initialMetaForTable } from "constants/common";
import {
  getCounties,
  getHealthPlans,
  getStates,
} from "containers/policies/api";
import { getUserByType } from "containers/users/api";
import React, { useCallback, useEffect, useState } from "react";
import {
  useDebouncedCaseManagerDataCallback,
  useDebouncedCountiesDataCallback,
  useDebouncedHealthPlansDataCallback,
  useDebouncedHousingNavigatorDataCallback,
  useDebouncedStatesDataCallback,
} from "utils/debouncedHelper";
import AsyncSelect from "components/common/select/AsyncSelect";
import { capitalizeName } from "utils/common";
export default function MemberFilter({
  filters,
  setFilters,
  meta,
  setMeta,
  setIsFilterModalVisible,
}) {
  const [statesList, setStatesList] = useState([]);
  const [countiesList, setCountiesList] = useState([]);
  const [healthPlansList, setHealthPlansList] = useState([]);
  const [
    housingNavigatorsList,
    setHousingNavigatorsList,
  ] = useState([]);
  const [caseManagersList, setCaseManagersList] = useState([]);

  const debouncedHealthPlansDataCallback = useDebouncedHealthPlansDataCallback();
  const debouncedStatesDataCallback = useDebouncedStatesDataCallback();
  const debouncedCoutiesDataCallback = useDebouncedCountiesDataCallback();
  const debouncedHousingNavigatorDataCallback = useDebouncedHousingNavigatorDataCallback();
  const debouncedCaseManagerCallback = useDebouncedCaseManagerDataCallback();

  const fetchStates = useCallback(async () => {
    try {
      const result = await getStates({ ...initialMetaForTable });
      const options =
        result?.states?.map((st) => ({
          value: st.id,
          label: st.name.toUpperCase(),
        })) || [];
      options.sort((a, b) => a.label.localeCompare(b.label))
      setStatesList(options);

      // Default State to CA when available
      const caState =
        options.find(
          (opt) =>
            opt.label?.toLowerCase() === "california" ||
            opt.label === "CA",
        ) || null;

      if (caState) {
        setFilters((prev) => ({
          ...prev,
          state: prev.state || caState,
        }));
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  }, []);

  const fetchCounties = useCallback(async (stateOption) => {
    if (!stateOption?.value) {
      setCountiesList([]);
      return;
    }
    try {
      const result = await getCounties({
        ...initialMetaForTable,
        state_id: stateOption.value,
      });
      const options =
        result?.counties?.map((ct) => ({
          value: ct.id,
          label: capitalizeName(ct.name),
        })) || [];
      options.sort((a, b) => a.label.localeCompare(b.label))
      setCountiesList(options);
    } catch (error) {
      console.error("Error fetching counties:", error);
    }
  }, []);

  const fetchHealthPlans = useCallback(async () => {
    try {
      const result = await getHealthPlans({
        ...initialMetaForTable,
      });
      const options =
        result?.health_plans?.map((hp) => ({
          value: hp.id,
          label: hp.name,
        })) || [];
      options.sort((a, b) => a.label.localeCompare(b.label))
      setHealthPlansList(options);
    } catch (error) {
      console.error("Error fetching health plans:", error);
    }
  }, []);

  const fetchCaseManagers = useCallback(async () => {
    try {
      const result = await getUserByType({
        user_type: "case_manager",
        ...initialMetaForTable,
      });
      const options =
        result?.cs_managers?.map((user) => ({
          value: user.id,
          label: user.full_name,
        })) || [];
      options.sort((a, b) => a.label.localeCompare(b.label))
      setCaseManagersList(options);
    } catch (error) {
      console.error("Error fetching case managers:", error);
    }
  }, []);

  const fetchHousingNavigators = useCallback(async () => {
    try {
      const result = await getUserByType({
        user_type: "housing_navigator",
        ...initialMetaForTable,
      });
      const options =
        result?.housing_navigators?.map((user) => ({
          value: user.id,
          label: user.full_name,
        })) || [];
      options.sort((a, b) => a.label.localeCompare(b.label))
      setHousingNavigatorsList(options);
    } catch (error) {
      console.error("Error fetching housing navigators:", error);
    }
  }, []);

  useEffect(() => {
    fetchStates();
    fetchHealthPlans();
    fetchHousingNavigators();
    fetchCaseManagers();
  }, []);
  useEffect(() => {
    if (filters.state) {
      fetchCounties(filters.state);
    } else {
      setCountiesList([]);
    }
  }, [filters.state, fetchCounties]);
  const handleClearFilters = () => {
    const caState =
      statesList.find(
        (opt) =>
          opt.label?.toLowerCase() === "california" ||
          opt.label === "CA",
      ) || null;

    setFilters({
      state: caState,
      county: null,
      healthPlan: null,
      housingNavigator: null,
      caseManager: null,
    });

    setMeta((prev) => ({
      ...prev,
      page: 1,
      state_id: caState?.value || "",
      county_id: "",
      health_plan_id: "",
      housing_navigator_id: "",
      case_manager_id: "",
    }));

    // handleRefreshPage();
  };
  const handleApplyFilters = () => {
    setMeta((prev) => ({
      ...prev,
      page: 1,
      filters: {
        state_id: filters.state || "",
        county_id: filters.county || "",
        health_plan_id: filters.healthPlan || "",
        housing_navigator_id: filters.housingNavigator || "",
        case_manager_id: filters.caseManager || "",
      }
    }));

    // handleRefreshPage();
    setIsFilterModalVisible(false);
  };

  return (
    <div className="question-modal">
      <hr className="custom-modal-line mt-0" />
      <form
        className="form-main"
        onSubmit={(e) => {
          e.preventDefault();
          handleApplyFilters();
        }}
      >
        <div className="input-flex row">
          <div className="col-sm-12">
            <AsyncSelect
              label="State"
              name="state"
              isClearable
              options={statesList}
              loadOptions={(inputValue) =>
                new Promise((resolve) => {
                  debouncedStatesDataCallback(
                    inputValue,
                    resolve,
                  );
                })
              }
              selectedValue={filters.state}
              placeholder="Select State"
              handleChange={(option) => {
                fetchCounties(option);

                setFilters((prev) => ({
                  ...prev,
                  state: option,
                  county: null,
                }));
              }}
            />
          </div>
        </div>

        <div className="input-flex row">
          <div className="col-sm-12">
            <AsyncSelect
              isClearable
              label="County"
              name="county"
              options={countiesList}
              loadOptions={(inputValue) =>
                new Promise((resolve) => {
                  debouncedCoutiesDataCallback(
                    inputValue,
                    filters.state?.value,
                    resolve,
                  );
                })
              }
              selectedValue={filters.county}
              placeholder="Select County"
              handleChange={(option) => {
                setFilters((prev) => ({
                  ...prev,
                  county: option,
                }));
              }}
              disabled={!filters.state}
            />
          </div>
        </div>

        <div className="input-flex row">
          <div className="col-sm-12">
            <AsyncSelect
              isClearable
              label="Health Plan"
              name="healthPlan"
              options={healthPlansList}
              loadOptions={(inputValue) =>
                new Promise((resolve) => {
                  debouncedHealthPlansDataCallback(
                    inputValue,
                    resolve,
                  );
                })
              }
              selectedValue={filters.healthPlan}
              placeholder="Select Health Plan"
              handleChange={(option) => {
                setFilters((prev) => ({
                  ...prev,
                  healthPlan: option?.value,
                }));
              }}
            />
          </div>
        </div>

        <div className="input-flex row">
          <div className="col-sm-12">
            <AsyncSelect
              isClearable
              label="Housing Navigator"
              name="housingNavigator"
              options={housingNavigatorsList}
              loadOptions={(inputValue) =>
                new Promise((resolve) => {
                  debouncedHousingNavigatorDataCallback(
                    inputValue,
                    resolve,
                  );
                })
              }
              selectedValue={filters.housingNavigator}
              placeholder="Select Housing Navigator"
              handleChange={(option) => {
                setFilters((prev) => ({
                  ...prev,
                  housingNavigator: option?.value,
                }));
              }}
            />
          </div>
        </div>

        <div className="input-flex row">
          <div className="col-sm-12">
            <AsyncSelect
              isClearable
              label="CSS Manager"
              name="caseManager"
              options={caseManagersList}
              loadOptions={(inputValue) =>
                new Promise((resolve) => {
                  debouncedCaseManagerCallback(
                    inputValue,
                    resolve,
                  );
                })
              }
              selectedValue={filters.caseManager}
              placeholder="Select CSS Manager"
              handleChange={(option) => {
                setFilters((prev) => ({
                  ...prev,
                  caseManager: option?.value,
                }));
              }}
            />
          </div>
        </div>

        <hr className="custom-modal-line mt-4" />
        <div className="action gap-2">
          <button
            className="secondary-btn w-50"
            type="button"
            onClick={handleClearFilters}
          >
            Clear
          </button>
          <button className="primary-btn w-50" type="submit">
            Apply
          </button>
        </div>
      </form>
    </div>
  );
}
